import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DsButton, DsInput, DsModal } from '@/modules/internal-v2/ds';
import type { ItemWithAllPrices } from '@/api/items/items.types';
import { useBulkEditItemSpecs } from '@/api/specifications/specifications.hooks';
import type {
  BulkEditItemSpecsRequest,
  BulkEditResult,
  DeviceCategory,
  SpecApplyMode,
  SpecValueChange,
} from '@/api/specifications/specifications.types';
import {
  ATTRIBUTE_SPECS_BY_CATEGORY,
  type AttributeSpec,
} from './spec-attribute-config';
import './bulk-spec-edit-modal.css';
import './spec-edit-modal.css';

interface BulkSpecEditModalProps {
  open: boolean;
  items: ItemWithAllPrices[];
  onClose: () => void;
  onApplied: () => void;
}

/**
 * Bulk-edit wizard: the same field changes are applied to every selected
 * item. Each editable field starts as 'no change' — only fields the user
 * explicitly enables via the "Ändern" checkbox are sent to the backend.
 * That mirrors the Magento mass-action UX: opt-in per-attribute change.
 *
 * Category is *not* editable here — it is derived from the item's SAP
 * item-group. Selections that span multiple categories can only edit fields
 * that are common to all of them (today: none); the UI surfaces the warning
 * and disables the form when the selection is heterogeneous.
 */
export function BulkSpecEditModal({
  open,
  items,
  onClose,
  onApplied,
}: BulkSpecEditModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const bulkEdit = useBulkEditItemSpecs();

  const scope = useMemo(() => analyseScope(items), [items]);

  const [specChanges, setSpecChanges] = useState<
    Record<string, { enabled: boolean; draft: BulkSpecDraft }>
  >({});
  const [preview, setPreview] = useState<BulkEditResult | null>(null);

  const effectiveCategory: DeviceCategory | null = scope.singleCategory;
  const editableAttrs = effectiveCategory
    ? ATTRIBUTE_SPECS_BY_CATEGORY[effectiveCategory]
    : [];

  const buildRequest = (dryRun: boolean): BulkEditItemSpecsRequest => {
    const body: BulkEditItemSpecsRequest = {
      itemIds: items.map((i) => i.id),
      dryRun,
    };
    const specs: Record<string, SpecValueChange> = {};
    for (const [key, { enabled, draft }] of Object.entries(specChanges)) {
      if (!enabled) continue;
      const v = toSpecValueChange(draft);
      if (v) specs[key] = v;
    }
    if (Object.keys(specs).length > 0) body.specifications = specs;
    return body;
  };

  const hasAnyChange = (req: BulkEditItemSpecsRequest) =>
    req.productLine !== undefined ||
    req.model !== undefined ||
    (req.specifications && Object.keys(req.specifications).length > 0);

  const handlePreview = async () => {
    const body = buildRequest(true);
    if (!hasAnyChange(body)) {
      enqueueSnackbar('Bitte mindestens eine Änderung aktivieren.', {
        variant: 'warning',
      });
      return;
    }
    try {
      const res = await bulkEdit.mutateAsync(body);
      setPreview(res);
    } catch (e) {
      enqueueSnackbar(`Vorschau fehlgeschlagen: ${(e as Error).message}`, {
        variant: 'error',
      });
    }
  };

  const handleApply = async () => {
    const body = buildRequest(false);
    if (!hasAnyChange(body)) return;
    try {
      const res = await bulkEdit.mutateAsync(body);
      const proposedCount =
        res.proposed.productLines.length +
        res.proposed.models.length +
        res.proposed.vocabulary.length;
      enqueueSnackbar(
        `${res.affected} Artikel aktualisiert${proposedCount > 0 ? `, ${proposedCount} neue Vorschläge` : ''}${res.skipped.length > 0 ? `, ${res.skipped.length} übersprungen` : ''}.`,
        { variant: 'success' },
      );
      onApplied();
    } catch (e) {
      enqueueSnackbar(`Anwenden fehlgeschlagen: ${(e as Error).message}`, {
        variant: 'error',
      });
    }
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title={`Bulk-Bearbeitung · ${items.length} Artikel`}
      description={renderScopeDescription(scope)}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose}>
            Schließen
          </DsButton>
          <DsButton
            variant="default"
            onClick={handlePreview}
            disabled={bulkEdit.isPending || !effectiveCategory}
          >
            Vorschau (Dry-Run)
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleApply}
            disabled={bulkEdit.isPending || !effectiveCategory}
          >
            {bulkEdit.isPending ? 'Speichert…' : 'Anwenden'}
          </DsButton>
        </>
      }
    >
      <div className="bulk-edit-form">
        {scope.manufacturers.length > 1 && (
          <div className="bulk-edit-warning">
            Auswahl umfasst {scope.manufacturers.length} Hersteller. Vokabular
            ist herstellerspezifisch — solche Änderungen werden für Artikel mit
            anderem Hersteller übersprungen.
          </div>
        )}
        {scope.categories.length > 1 && (
          <div className="bulk-edit-warning">
            Auswahl umfasst {scope.categories.length} Kategorien (
            {scope.categories.join(', ')}). Bulk-Bearbeitung ist nur möglich,
            wenn alle ausgewählten Artikel dieselbe Gerätekategorie haben —
            bitte Auswahl einschränken.
          </div>
        )}
        {scope.categories.length === 0 && (
          <div className="bulk-edit-warning">
            Keine der ausgewählten Artikel ist einer Gerätekategorie zugeordnet
            (die Warengruppe ist nicht „Smartphone/Tablet/Smartwatch/Notebook").
            Spezifikationen können nicht bearbeitet werden.
          </div>
        )}

        {/* Spec attribute rows */}
        {effectiveCategory &&
          editableAttrs.map((spec) => (
            <SpecRow
              key={spec.key}
              spec={spec}
              state={specChanges[spec.key]}
              onChange={(state) =>
                setSpecChanges((prev) => ({ ...prev, [spec.key]: state }))
              }
            />
          ))}

        {preview && (
          <div className="bulk-edit-preview">
            <h4>Vorschau</h4>
            <ul>
              <li>
                <strong>{preview.affected}</strong> Artikel werden geändert
              </li>
              {Object.entries(preview.perAttribute).map(([k, n]) => (
                <li key={k}>
                  <code>{k}</code>: {n} Artikel
                </li>
              ))}
              {preview.proposed.productLines.length > 0 && (
                <li>
                  Neue Produktlinien:{' '}
                  {preview.proposed.productLines.map((p) => p.name).join(', ')}
                </li>
              )}
              {preview.proposed.models.length > 0 && (
                <li>
                  Neue Modelle:{' '}
                  {preview.proposed.models.map((m) => m.name).join(', ')}
                </li>
              )}
              {preview.proposed.vocabulary.length > 0 && (
                <li>
                  Neue Vokabel-Werte:{' '}
                  {preview.proposed.vocabulary
                    .map((v) => `${v.attribute}=${v.value}`)
                    .join(', ')}
                </li>
              )}
              {preview.skipped.length > 0 && (
                <li className="bulk-edit-skipped">
                  <strong>{preview.skipped.length}</strong> übersprungen
                  <details>
                    <summary>Details</summary>
                    <ul>
                      {preview.skipped.slice(0, 20).map((s, i) => (
                        <li key={i}>
                          <code>{s.itemId}</code>: {s.reason}
                        </li>
                      ))}
                      {preview.skipped.length > 20 && (
                        <li>… +{preview.skipped.length - 20} weitere</li>
                      )}
                    </ul>
                  </details>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </DsModal>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

type BulkSpecDraft =
  | { kind: 'clear'; mode: SpecApplyMode }
  | { kind: 'mechanical'; value: string; mode: SpecApplyMode }
  | { kind: 'vocabulary-propose'; value: string; mode: SpecApplyMode };

interface ScopeAnalysis {
  manufacturers: { id: number; name: string }[];
  categories: string[];
  singleManufacturerId: number | null;
  singleCategory: DeviceCategory | null;
}

function analyseScope(items: ItemWithAllPrices[]): ScopeAnalysis {
  const mfrMap = new Map<number, string>();
  const cats = new Set<string>();
  for (const item of items) {
    mfrMap.set(item.manufacturerId, item.manufacturer);
    const c = item.identity?.category;
    if (c) cats.add(c);
  }
  const manufacturers = Array.from(mfrMap.entries()).map(([id, name]) => ({
    id,
    name,
  }));
  return {
    manufacturers,
    categories: Array.from(cats),
    singleManufacturerId:
      manufacturers.length === 1 ? manufacturers[0].id : null,
    singleCategory: cats.size === 1 ? (Array.from(cats)[0] as DeviceCategory) : null,
  };
}

function renderScopeDescription(scope: ScopeAnalysis): string {
  const parts: string[] = [];
  parts.push(
    scope.manufacturers.length === 1
      ? `Hersteller: ${scope.manufacturers[0].name}`
      : `${scope.manufacturers.length} Hersteller`,
  );
  parts.push(
    scope.categories.length === 0
      ? 'Kategorie unbekannt'
      : scope.categories.length === 1
        ? `Kategorie: ${scope.categories[0]}`
        : `${scope.categories.length} Kategorien`,
  );
  return parts.join(' · ');
}

function toSpecValueChange(draft: BulkSpecDraft): SpecValueChange | null {
  switch (draft.kind) {
    case 'clear':
      return { type: 'clear', mode: draft.mode };
    case 'mechanical':
      return draft.value.trim()
        ? { type: 'mechanical', value: draft.value, mode: draft.mode }
        : null;
    case 'vocabulary-propose':
      return draft.value.trim()
        ? { type: 'vocabulary-propose', value: draft.value, mode: draft.mode }
        : null;
  }
}

// ── row primitives ─────────────────────────────────────────────────────

function FieldRow({
  label,
  enabled,
  onEnabledChange,
  children,
}: {
  label: string;
  enabled: boolean;
  onEnabledChange: (b: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className={`bulk-edit-row${enabled ? ' is-enabled' : ''}`}>
      <label className="bulk-edit-enable">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        <span>Ändern</span>
      </label>
      <span className="bulk-edit-label">{label}</span>
      <div className="bulk-edit-control">{children}</div>
    </div>
  );
}

interface SpecRowProps {
  spec: AttributeSpec;
  state?: { enabled: boolean; draft: BulkSpecDraft };
  onChange: (state: { enabled: boolean; draft: BulkSpecDraft }) => void;
}

function SpecRow({ spec, state, onChange }: SpecRowProps) {
  const enabled = state?.enabled ?? false;
  const draft: BulkSpecDraft = state?.draft ?? defaultDraft(spec);

  return (
    <FieldRow
      label={labelForAttribute(spec.key)}
      enabled={enabled}
      onEnabledChange={(en) => onChange({ enabled: en, draft })}
    >
      <div className="bulk-edit-spec-control">
        <SpecControl
          spec={spec}
          draft={draft}
          disabled={!enabled}
          onChange={(d) => onChange({ enabled, draft: d })}
        />
        <select
          className="bulk-edit-mode"
          disabled={!enabled}
          value={draft.mode}
          onChange={(e) =>
            onChange({
              enabled,
              draft: { ...draft, mode: e.target.value as SpecApplyMode },
            })
          }
          title="Apply mode"
        >
          <option value="set">Überschreiben</option>
          <option value="fill-if-empty">Nur wenn leer</option>
        </select>
      </div>
    </FieldRow>
  );
}

function defaultDraft(spec: AttributeSpec): BulkSpecDraft {
  if (spec.class === 'semantic-governed') {
    // bulk vocabulary only supports propose-new (selecting an existing id
    // makes no sense when items may span manufacturers; the user picks a
    // single literal value and the backend resolves it per manufacturer).
    return { kind: 'vocabulary-propose', value: '', mode: 'set' };
  }
  return { kind: 'mechanical', value: '', mode: 'set' };
}

function SpecControl({
  spec,
  draft,
  disabled,
  onChange,
}: {
  spec: AttributeSpec;
  draft: BulkSpecDraft;
  disabled: boolean;
  onChange: (d: BulkSpecDraft) => void;
}) {
  if (spec.class === 'mechanical-closed-enum') {
    const current = draft.kind === 'mechanical' ? draft.value : '';
    return (
      <select
        className="spec-edit-select"
        disabled={disabled}
        value={draft.kind === 'clear' ? '__clear__' : current}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__clear__') onChange({ kind: 'clear', mode: draft.mode });
          else onChange({ kind: 'mechanical', value: v, mode: draft.mode });
        }}
      >
        <option value="">— Wert wählen —</option>
        {spec.enumValues?.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
        <option value="__clear__">— Wert leeren —</option>
      </select>
    );
  }

  const value =
    draft.kind === 'clear'
      ? ''
      : draft.kind === 'mechanical' || draft.kind === 'vocabulary-propose'
        ? draft.value
        : '';

  return (
    <div className="bulk-edit-input-row">
      <DsInput
        disabled={disabled}
        value={value}
        placeholder={
          spec.class === 'semantic-governed'
            ? 'Neuen / bestehenden Wert eingeben…'
            : (hintFor(spec.key) ?? 'Wert…')
        }
        onChange={(e) => {
          const v = e.target.value;
          if (spec.class === 'semantic-governed') {
            onChange({
              kind: 'vocabulary-propose',
              value: v,
              mode: draft.mode,
            });
          } else {
            onChange({ kind: 'mechanical', value: v, mode: draft.mode });
          }
        }}
      />
      <DsButton
        variant="ghost"
        disabled={disabled}
        onClick={() => onChange({ kind: 'clear', mode: draft.mode })}
        title="Wert leeren"
      >
        —
      </DsButton>
    </div>
  );
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  storage: 'Speicher',
  ram: 'RAM',
  year: 'Jahr',
  connectivity: 'Konnektivität',
  region: 'Region',
  condition: 'Zustand',
  color: 'Farbe',
  chip: 'Chipsatz',
  screenSize: 'Display',
  caseSize: 'Gehäusegröße',
  caseColor: 'Gehäusefarbe',
  caseMaterial: 'Gehäusematerial',
  band: 'Armband',
  bandSize: 'Armband-Größe',
  cpuCores: 'CPU-Kerne',
  gpuCores: 'GPU-Kerne',
  keyboardLayout: 'Tastaturlayout',
};
const ATTRIBUTE_HINTS: Record<string, string> = {
  storage: 'z. B. 256GB, 1TB',
  ram: 'z. B. 8GB, 32GB',
  screenSize: 'z. B. 13.6", 15"',
  caseSize: 'z. B. 42mm',
  year: 'z. B. 2024',
  cpuCores: 'z. B. 10-core',
  gpuCores: 'z. B. 16-core',
};
function labelForAttribute(key: string): string {
  return ATTRIBUTE_LABELS[key] ?? key;
}
function hintFor(key: string): string | null {
  return ATTRIBUTE_HINTS[key] ?? null;
}
